import { computed, readonly, ref, watch } from 'vue'
import { listEstablishments } from 'src/modules/structures/api'
import { empty } from 'src/utils/variable'
import { useRouter } from 'vue-router'
import { listMyPatientFiles } from 'src/modules/patient-files/api'
import { getCssVar, setCssVar, useQuasar } from 'quasar'
import { isAdmin, isProfessional, isTenantAdmin, requiredIdentityProvider, user } from 'src/services/session.js'
import { setBrowserThemeColor } from 'src/utils/composables/domUtils.js'
import { useDevice } from 'src/modules/core/composables/useDevice.js'

const allPatientFiles = ref([])
const ownPatientFiles = ref([])
const patientFilesAsCaregiver = ref([])
const nonAwaitingPatientFiles = ref([])
const awaitingPatientFiles = ref([])
const activePatientFile = ref(null)
const allStructures = ref([])
/**
 * @type {{value: EstablishmentData}}
 */
const activeStructure = ref(null)
let loadPatientFileCalled = false
let loadStructureCalled = false

const COLORS_NAME = ['primary', 'secondary', 'accent', 'dark', 'positive', 'negative', 'info', 'warning']
export const DEFAULT_COLORS = Object.fromEntries(COLORS_NAME.map(colorName => [colorName, getCssVar(colorName)]))

let context = null

export function useContext() {
  if (context !== null) {
    return context
  }

  const router = useRouter()
  const $q = useQuasar()
  const { isNativeCallAvailable, callNative } = useDevice($q)

  const asPatient = computed(() => {
    return !!activePatientFile.value
  })
  const asProfessional = computed(() => {
    return isProfessional.value && !asCaregiver.value
  })
  const asCaregiver = computed(() => {
    return asPatient.value && activePatientFile.value?.patient?.identifier !== user.value.identifier
  })

  async function onLoadPatientFiles() {
    if (loadPatientFileCalled) {
      throw new Error('You MUST NOT call this method more than one time.')
    }

    loadPatientFileCalled = true
    allPatientFiles.value = (await listMyPatientFiles({ page: 1, rowsPerPage: 200 })).data.items
    // the API doesn't return the patient key if it's the patient file of the user who make the call
    ownPatientFiles.value = allPatientFiles.value.filter(patientFile => patientFile.patient === undefined)
    patientFilesAsCaregiver.value = allPatientFiles.value.filter(patientFile => patientFile.patient !== undefined)
    nonAwaitingPatientFiles.value = allPatientFiles.value.filter(patientFile => patientFile.status === 'OPEN' || patientFile.status === 'CLOSED')
    awaitingPatientFiles.value = allPatientFiles.value.filter(patientFile => patientFile.status === 'AWAITING')

    // Don't use vue router current route because plugin is still loading and params are not computed yet
    const urlParts = window.location.pathname.split('/')
    if (urlParts.length >= 2 && urlParts[1] === 'p' && Number.isInteger(parseInt(urlParts[2])) && !empty(allPatientFiles.value[parseInt(urlParts[2])])) {
      activePatientFile.value = allPatientFiles.value[parseInt(urlParts[2])]
    }

    // Select first patient file if there is only one
    router.beforeEach((to, from, next) => {
      if (
        to.name === 'context-selector' &&
        empty(activePatientFile.value) &&
        nonAwaitingPatientFiles.value.length === 1 &&
        allStructures.value.length === 0
      ) {
        activePatientFile.value = nonAwaitingPatientFiles.value[0]
        next('/p/0')
      } else {
        next()
      }
    })

    // Current active structure is set by navigation
    router.beforeEach((to, from, next) => {
      let patientFileIndex = null

      // Selected structure index is present in route params and valid
      if (!empty(to.params) && !empty(to.params.patientFileIndex) && !isNaN(parseInt(to.params.patientFileIndex, 10))) {
        patientFileIndex = to.params.patientFileIndex

        // Structure index points to an existing structure of the user
        if (patientFileIndex && !empty(allPatientFiles.value[patientFileIndex])) {
          // It differs from the active structure (none or another one)
          if (activePatientFile.value !== allPatientFiles.value[patientFileIndex]) {
            activePatientFile.value = allPatientFiles.value[patientFileIndex]
          }
          next()
        } else {
          // The user does not have this structure in his list
          next({ name: 'context-selector' })
        }
      } else {
        // No structure required for the target route
        next()
      }
    })

    router.afterEach((to) => {
      if (empty(to.params) || empty(to.params.patientFileIndex) || isNaN(parseInt(to.params.patientFileIndex, 10))) {
        activePatientFile.value = null
      }
    })
  }

  async function updatePatientFiles() {
    allPatientFiles.value = (await listMyPatientFiles({ page: 1, rowsPerPage: 100 })).data.items
    nonAwaitingPatientFiles.value = allPatientFiles.value.filter(patientFile => patientFile.status === 'OPEN' || patientFile.status === 'CLOSED')
    awaitingPatientFiles.value = allPatientFiles.value.filter(patientFile => patientFile.status === 'AWAITING')
  }

  function isIdOfOwnPatientFile(id) {
    return ownPatientFiles.value.some(patientFile => patientFile.id === id)
  }

  async function onLoadStructures() {
    if (loadStructureCalled) {
      throw new Error('You MUST NOT call this method more than one time.')
    }

    loadStructureCalled = true
    allStructures.value = (await listEstablishments()).data.items

    // Don't use vue router current route because plugin is still loading and params are not computed yet
    const urlParts = window.location.pathname.split('/')
    if (urlParts.length >= 2 && urlParts[1] === 's' && Number.isInteger(parseInt(urlParts[2])) && !empty(allStructures.value[parseInt(urlParts[2])])) {
      activeStructure.value = allStructures.value[parseInt(urlParts[2])]
    }

    // Select first structure if there is only one
    router.beforeEach((to, from, next) => {
      if( to.name !== 'context-selector' || !empty(activeStructure.value)) {
        return next()
      }

      if(requiredIdentityProvider.value) {
        const structuresMatchingIdentityProvider = allStructures.value
          .filter(structure =>
            Array.isArray(structure.allowedIdentityProviders) ? structure.allowedIdentityProviders.includes(requiredIdentityProvider.value) : false
          )

        if(structuresMatchingIdentityProvider.length === 1) {
          return next('/s/'+allStructures.value.indexOf(structuresMatchingIdentityProvider[0]))
        }
      }

      if (
        allStructures.value.length === 1 &&
        allPatientFiles.value.length === 0
      ) {
        activeStructure.value = allStructures.value[0]
        next('/s/0')
      } else {
        next()
      }
    })

    // Current active structure is set by navigation
    router.beforeEach((to, from, next) => {
      let structureIndex = null

      // Selected structure index is present in route params and valid
      if (!empty(to.params) && !empty(to.params.structureIndex) && !isNaN(parseInt(to.params.structureIndex, 10))) {
        structureIndex = to.params.structureIndex

        // Structure index points to an existing structure of the user
        if (structureIndex && !empty(allStructures.value[structureIndex])) {
          // It differs from the active structure (none or another one)
          if (activeStructure.value !== allStructures.value[structureIndex]) {
            activeStructure.value = allStructures.value[structureIndex]
          }
          next()
        } else {
          // The user does not have this structure in his list
          next({ name: 'context-selector' })
        }
      } else {
        // No structure required for the target route
        next()
      }
    })

    router.afterEach((to) => {
      if (empty(to.params) || empty(to.params.structureIndex) || isNaN(parseInt(to.params.structureIndex, 10))) {
        activeStructure.value = null
      }
    })
  }

  watch([activeStructure, activePatientFile, () => $q.dark.isActive], async () => {
    const contextEstablishment = activeStructure.value ? activeStructure.value : activePatientFile.value?.service?.establishment
    for (const colorName of COLORS_NAME) {
      if (!isAdmin.value && !isTenantAdmin.value) {
        setCssVar(
          colorName,
          contextEstablishment?.theme?.[$q.dark.isActive ? 'dark' : 'light']?.[colorName] ||
          DEFAULT_COLORS[colorName] ||
          ''
        )
        if(colorName === 'primary') {
          const color = contextEstablishment?.theme?.[$q.dark.isActive ? 'dark' : 'light']?.[colorName] || ''
          setBrowserThemeColor(color)
          if (isNativeCallAvailable()) {
            await callNative('setThemeColor', JSON.stringify({ themeColor: color }), null)
          }
        }
      }
    }
  }, {
    immediate: true
  })

  const activePatientFileHasProfessionalDirectoryEnabled = computed(() => {
    return activePatientFile.value && activePatientFile.value.service.establishment.professionalDirectoryEnabled === true
  })

  context = {
    onLoadPatientFiles,
    allPatientFiles,
    activePatientFile: readonly(activePatientFile),
    onLoadStructures,
    updatePatientFiles,
    allStructures,
    activeStructure: readonly(activeStructure),
    awaitingPatientFiles,
    nonAwaitingPatientFiles,
    isIdOfOwnPatientFile,
    asPatient,
    asProfessional,
    asCaregiver,
    activePatientFileHasProfessionalDirectoryEnabled
  }

  return context
}
